import classNames from "classnames";
import {Children, cloneElement, forwardRef, ReactElement, useEffect, useRef, useState} from "react";

import {InputProps} from "@/components/input/Input.types.ts";
import {InputGroupProps} from "@/components/inputGroup/InputGroup.types.ts";
import {InputAddon} from "@/components/inputGroup/subComponents/inputAddon/InputAddon.tsx";

import {InputElement} from "./subComponents/inputElement/InputElement";

import styles from "./InputGroup.module.scss";

// This component is meant to be specifically used with the Input component.
// It is not meant to be used with any other component.
// This would create an input group where you can add elements and addons to the start and end of the input.
// If you do not need these additional elements, you should use the Input component directly.
export const InputGroup = forwardRef<HTMLDivElement, InputGroupProps>(
  (
    {
      startElement,
      startElementProps,
      endElement,
      endElementProps,
      startAddon,
      startAddonProps,
      endAddon,
      endAddonProps,
      endOffset = "0px",
      startOffset = "0px",
      children,
      className,
      disabled,
      "aria-invalid": ariaInvalid,
      ...rest
    },
    ref
  ) => {
    const child = Children.only<ReactElement<InputProps>>(children);

    const startAddonRef = useRef<HTMLDivElement>(null);
    const endAddonRef = useRef<HTMLDivElement>(null);
    const [startElementOffset, setStartElementOffset] = useState<string>("0px");
    const [endElementOffset, setEndElementOffset] = useState<string>("0px");

    useEffect(() => {
      const updateOffset = () => {
        if (startAddon && startElement && startAddonRef.current) {
          const startElementOffset = startAddonRef.current.offsetWidth;
          setStartElementOffset(`${startElementOffset}px`);
        }

        if (endAddon && endElement && endAddonRef.current) {
          const endElementOffset = endAddonRef.current.offsetWidth;
          setEndElementOffset(`${endElementOffset}px`);
        }
      };

      updateOffset();

      // window.addEventListener("resize", updateOffset);
      //
      // return () => {
      //   window.removeEventListener("resize", updateOffset);
      // };
    }, [startAddon, startElement, endAddon, endElement]);

    return (
      <div className={classNames("input-group", styles.inputGroup, className)} ref={ref} {...rest}>
        {startAddon && (
          <InputAddon ref={startAddonRef} disabled={disabled} aria-invalid={ariaInvalid} {...startAddonProps}>
            {startAddon}
          </InputAddon>
        )}
        {startElement && (
          <InputElement
            offSet={startElementOffset}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            {...startElementProps}
          >
            {startElement}
          </InputElement>
        )}
        {cloneElement(child, {
          style: {
            ...(startElement ? {paddingLeft: `calc(40px + ${startOffset})`} : {}),
            ...(endElement ? {paddingRight: `calc(40px + ${endOffset})`} : {}),
          },
          disabled: disabled,
          "aria-invalid": ariaInvalid,
          ...children.props,
        })}
        {endElement && (
          <InputElement
            placement={"end"}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            offSet={endElementOffset}
            {...endElementProps}
          >
            {endElement}
          </InputElement>
        )}
        {endAddon && (
          <InputAddon ref={endAddonRef} disabled={disabled} aria-invalid={ariaInvalid} {...endAddonProps}>
            {endAddon}
          </InputAddon>
        )}
      </div>
    );
  }
);
